// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { Config, ConfigSection, UpdateConfigRequest } from '../../types/config';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmDialog from '../ConfirmDialog';
import UnsavedChangesModal from '../UnsavedChangesModal';
import './Configuration.scss';

const Configuration: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editedValues, setEditedValues] = useState<{ [key: string]: string }>({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});

  const userRole = authService.getSessionRole();
  const canEditConfig = userRole === UserRole.USER_ROLE_ADMIN;

  // Track unsaved changes
  const hasUnsavedChanges = Object.keys(editedValues).length > 0;
  const {
    showUnsavedChangesModal,
    isSaving,
    confirmLeave,
    saveAndLeave,
    cancelLeave
  } = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: async () => {
      await handleSaveConfig();
    },
    message: 'You have unsaved configuration changes. Are you sure you want to leave without saving?'
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await consoleService.getConfig();
      
      // Debug: Log the actual API response structure
      console.log('Config API response:', data);
      
      setConfig(data);
      
      // Initialize expanded sections (expand all by default)
      const expanded: { [key: string]: boolean } = {};
      if (data && data.sections && Array.isArray(data.sections)) {
        data.sections.forEach((section: ConfigSection) => {
          expanded[section.name] = true;
        });
      } else {
        console.warn('Config data does not have expected sections array:', data);
      }
      setExpandedSections(expanded);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load configuration';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveConfig = async () => {
    if (Object.keys(editedValues).length === 0) {
      setShowSaveDialog(false);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const request: UpdateConfigRequest = {
        config: editedValues,
        restart_required: true
      };

      await consoleService.updateConfig(request);
      
      setEditedValues({});
      setShowSaveDialog(false);
      loadConfig(); // Reload to get updated values
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save configuration';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleResetChanges = () => {
    setEditedValues({});
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getFilteredSections = (): ConfigSection[] => {
    if (!config || !searchTerm) return config?.sections || [];

    return config.sections
      .map(section => ({
        ...section,
        values: section.values.filter(value =>
          value.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          value.value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter(section => section.values.length > 0);
  };

  if (loading) {
    return (
      <div className="configuration-container">
        <h2 className="pb-4">Configuration</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading configuration...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Configuration</h2>
        {canEditConfig && hasUnsavedChanges && (
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary"
              onClick={handleResetChanges}
            >
              Reset Changes
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowSaveDialog(true)}
            >
              Save Changes ({Object.keys(editedValues).length})
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {config?.warnings && config.warnings.length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Configuration Warnings:</strong>
          <ul className="mb-0 mt-2">
            {config.warnings.map((warning, index) => (
              <li key={index}>{typeof warning === 'string' ? warning : JSON.stringify(warning)}</li>
            ))}
          </ul>
        </div>
      )}

      {!canEditConfig && (
        <div className="alert alert-info mb-3">
          <strong>Read-only mode:</strong> You don't have permission to modify configuration values.
        </div>
      )}

      {/* Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Search Configuration</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by key or value..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="configuration-sections">
        {getFilteredSections().map((section) => (
          <div key={section.name} className="card mb-3">
            <div className="card-header">
              <button
                className="btn btn-link text-decoration-none p-0 w-100 text-start d-flex justify-content-between align-items-center"
                onClick={() => toggleSection(section.name)}
              >
                <h5 className="mb-0">{typeof section.name === 'string' ? section.name : JSON.stringify(section.name)}</h5>
                <i className={`fas fa-chevron-${expandedSections[section.name] ? 'up' : 'down'}`}></i>
              </button>
            </div>
            {expandedSections[section.name] && (
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>Key</th>
                        <th style={{ width: '50%' }}>Value</th>
                        {canEditConfig && <th style={{ width: '20%' }}>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {section.values.map((configValue) => {
                        const currentValue = editedValues[configValue.name] !== undefined 
                          ? editedValues[configValue.name] 
                          : (typeof configValue.value === 'string' ? configValue.value : JSON.stringify(configValue.value));
                        const hasChanged = editedValues[configValue.name] !== undefined;

                        return (
                          <tr key={configValue.name} className={hasChanged ? 'table-warning' : ''}>
                            <td>
                              <code className="config-key">{typeof configValue.name === 'string' ? configValue.name : JSON.stringify(configValue.name)}</code>
                              {configValue.warning && (
                                <div className="text-warning small mt-1">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  {typeof configValue.warning === 'string' ? configValue.warning : JSON.stringify(configValue.warning)}
                                </div>
                              )}
                            </td>
                            <td>
                              {canEditConfig ? (
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className={`form-control ${hasChanged ? 'border-warning' : ''}`}
                                    value={currentValue}
                                    onChange={(e) => handleValueChange(configValue.name, e.target.value)}
                                  />
                                  {hasChanged && (
                                    <span className="input-group-text bg-warning text-dark">
                                      <i className="fas fa-edit"></i>
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <code className="config-value">{currentValue}</code>
                              )}
                            </td>
                            {canEditConfig && (
                              <td>
                                {hasChanged && (
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                      const newValues = { ...editedValues };
                                      delete newValues[configValue.name];
                                      setEditedValues(newValues);
                                    }}
                                    title="Reset to original value"
                                  >
                                    <i className="fas fa-undo"></i>
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {getFilteredSections().length === 0 && (
        <div className="text-center py-4">
          <p className="text-muted">
            {searchTerm ? 'No configuration values match your search.' : 'No configuration available.'}
          </p>
          {config && !config.sections && (
            <div className="alert alert-warning mt-3">
              <strong>Debug Info:</strong> Configuration data received but no sections found.
              <details className="mt-2">
                <summary>Raw API Response</summary>
                <pre className="text-start mt-2">{JSON.stringify(config, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Save Configuration Dialog */}
      <ConfirmDialog
        isOpen={showSaveDialog}
        title="Save Configuration Changes"
        message={
          <div>
            <p>Are you sure you want to save the following configuration changes?</p>
            <div className="alert alert-warning">
              <strong>Warning:</strong> Saving configuration changes may require a server restart to take effect.
            </div>
            <div className="config-changes-preview">
              {Object.entries(editedValues).map(([key, value]) => (
                <div key={key} className="mb-2">
                  <strong>{typeof key === 'string' ? key : JSON.stringify(key)}:</strong> <code>{typeof value === 'string' ? value : JSON.stringify(value)}</code>
                </div>
              ))}
            </div>
          </div>
        }
        confirmText={saving ? 'Saving...' : 'Save Changes'}
        cancelText="Cancel"
        onConfirm={handleSaveConfig}
        onCancel={() => setShowSaveDialog(false)}
        confirmDisabled={saving}
        variant="warning"
      />

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        message="You have unsaved configuration changes. Are you sure you want to leave without saving?"
        onSaveAndLeave={saveAndLeave}
        onLeaveWithoutSaving={confirmLeave}
        onCancel={cancelLeave}
        isSaving={isSaving}
        showSaveOption={canEditConfig}
      />
    </div>
  );
};

export default Configuration;
